import { observable, action, makeObservable } from 'mobx';
import { TrpcClient } from 'src/appState/TrpcClient';
import { findAddress } from 'src_server/trpc/types/accountModel';

export interface AddressDetailsType {
    line1: string;
    line2: string;
    city: string;
    postalCode: string;
    countryIso2: string;
}

export class AddressSearchState {
    @observable public addresses: findAddress.AddressItemType[] = [];
    @observable public loadingAddresses: boolean = false;
    @observable public addressError: string | null = null;

    public constructor(private readonly trpc: TrpcClient) {
        makeObservable(this);
    }

    @action public clearAddresses = (): void => {
        this.addresses = [];
    };

    @action public clearError = (): void => {
        this.addressError = null;
    };

    public searchAddress = async (text: string): Promise<void> => {
        if (text.length === 0) {
            return;
        }

        this.setLoadingStart();

        try {
            const response = await this.trpc.client.accounts.findAddress.mutate({
                Countries: ['GB', 'IE'],
                Text: text,
            });

            if (response.responseStatus === 'success') {
                this.setSearchSuccess(response.data.Items);
            } else {
                this.setSearchError('Error, enter address manually');
            }
        } catch (error) {
            this.setSearchError('Error, enter address manually');
            console.error('searchAddress error:', error);
        }
    };

    public selectAddress = async (
        address: findAddress.AddressItemType,
        onAddressSelected: (details: AddressDetailsType) => void
    ): Promise<void> => {
        try {
            if (address.Type !== 'Address') {
                this.setLoadingTrue();

                const containerResponse = await this.trpc.client.accounts.findAddress.mutate({
                    Countries: [],
                    Text: '',
                    Container: address.Id,
                });

                if (containerResponse.responseStatus === 'success') {
                    this.setContainerSuccess(containerResponse.data.Items);
                } else {
                    this.setSearchError('Error loading addresses');
                }
                return;
            }

            this.setLoadingTrue();

            const addressDetailsResponse = await this.trpc.client.accounts.findAddressDetails.mutate({
                addressId: address.Id,
            });

            const firstItem = addressDetailsResponse.data.Items[0];
            if (firstItem === undefined) {
                this.setSearchError('No address details found');
                return;
            }

            if (firstItem.CountryIso2 === undefined) {
                this.setSearchError('Unknown country code');
                return;
            }

            this.setAddressSelectedSuccess(() => {
                onAddressSelected({
                    line1: firstItem.Line1 ?? '',
                    line2: firstItem.Line2 ?? '',
                    city: firstItem.City ?? '',
                    postalCode: firstItem.PostalCode ?? '',
                    countryIso2: firstItem.CountryIso2 ?? '',
                });
            });
        } catch (error) {
            this.setSearchError('Error, enter address manually');
        }
    };

    @action private setLoadingStart = (): void => {
        this.addressError = null;
        this.loadingAddresses = true;
    };

    @action private setSearchSuccess = (items: findAddress.AddressItemType[]): void => {
        this.addresses = items;
        this.loadingAddresses = false;
    };

    @action private setSearchError = (message: string): void => {
        this.addressError = message;
        this.loadingAddresses = false;
    };

    @action private setContainerSuccess = (items: findAddress.AddressItemType[]): void => {
        this.addresses = items;
        this.addressError = null;
        this.loadingAddresses = false;
    };

    @action private setLoadingTrue = (): void => {
        this.loadingAddresses = true;
    };

    @action private setAddressSelectedSuccess = (callback: () => void): void => {
        callback();
        this.clearAddresses();
        this.loadingAddresses = false;
    };
}
